/**************************************************************************************************/
/*   File: /src/app/shared/components/iframe/iframe.component.ts                                  */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 22nd 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Thu May 30 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { NGXLogger } from 'ngx-logger';
import { Subject, BehaviorSubject, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';

import {
    Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, AfterContentInit, OnDestroy
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../../environments/environment';

/**
 * The iFrame component
 */
@Component({
    selector: 'nwpl-frnt-iframe',
    templateUrl: './iframe.component.html',
    styleUrls: ['./iframe.component.scss']
})
export class IFrameComponent implements OnInit, AfterContentInit, OnChanges, OnDestroy {

    /**
     * The url
     */
    public url: SafeResourceUrl;

    /**
     * The language
     */
    @Input() language: string;

    /**
     * Is full screen
     */
    @Input() isFullScreen = false;

    /**
     * The planType
     */
    @Input() planType: string;

    /**
     * The ambiti array
     */
    @Input() ambiti: string[];

    /**
     * Map context identifier (optional)
     */
    @Input() contextId: string;

    /**
     * Dossier ID (optional)
     */
    @Input() dossierId: string;
    @Input() dossierExtent: string;

    /**
     * The disable flag
     */
    @Input() disable: boolean;

    /**
     * The selected
     */
    @Output() selected: EventEmitter<any> = new EventEmitter<any>();

    /**
     * The full screen event
     */
    @Output() fullScreened: EventEmitter<boolean> = new EventEmitter();

    /**
     * The iframe element 
     */
    @ViewChild('iframeElement') iframeElement: ElementRef<HTMLIFrameElement>;
    /**
     * The map ready flag
     */
    private mapReady: BehaviorSubject<boolean> = new BehaviorSubject(false);
    /**
     * The array of subscription
     */
    private subscriptions: Subscription[] = [];

    private hasValueChanged = (changes) => (key) => {
        const changesWithKey = changes[key]
        const changesDefinedOrNotNull = changesWithKey !== undefined || changesWithKey !== null
        const currentAndPreviouNotNullOrUndefined = changesWithKey && changesWithKey.currentValue !== null && changesWithKey.currentValue !== undefined && changesWithKey.previousValue !== null && changesWithKey.previousValue !== undefined
        const isChanged = changesWithKey && changesWithKey.currentValue !== changesWithKey.previousValue
        return !!(changes && changesDefinedOrNotNull && isChanged && currentAndPreviouNotNullOrUndefined);
    };

    /**
     * The contructor
     * @param route The active router
     * @param sanitizer The sanitizer
     * @param router The router
     */
    constructor(private route: ActivatedRoute, private sanitizer: DomSanitizer, private router: Router, private logger: NGXLogger) {

    }

    /**
     * The ng on init hook
     */
    ngOnInit() {
        // Create a unique URL each time so the iframe will detect the change
        //TODO da sistemare variabile requestedUrl
        const envPath = environment.name === '' ? 'prod' : environment.name;
        let requestedUrl = 'assets/map/' + envPath + '/#?lang=' + this.language;

        //Pass the Map Context Identifier if specified
        if ((typeof (this.contextId) !== 'undefined') && this.contextId !== null) {
            requestedUrl += "&ID_CONTEXT=" + this.contextId;
        }

        //Pass the Dossier Identifier if specified
        if ((typeof (this.dossierId) !== 'undefined') && this.dossierId !== null) {
            requestedUrl += "&ID_DOSSIER=" + this.dossierId;
        }
        //Pass the Dossier extent if specified
        if ((typeof (this.dossierExtent) !== 'undefined') && this.dossierExtent !== null) {
            requestedUrl += "&EXTENT_DOSSIER=" + this.dossierExtent;
        }
        if ((typeof (this.planType) !== 'undefined') && this.planType !== null) {
            requestedUrl += "&PLAN_TYPE_DOSSIER=" + this.planType;
        }
        //Pass the Dossier Identifier if specified
        // if ((typeof (this.dossierExtent) !== 'undefined') && this.dossierExtent !== null) {
        //     requestedUrl += "&EXTENT_DOSSIER=" + this.dossierExtent;
        // }

        console.log(requestedUrl);

        // Angular by default sanitises a URL, we need to bypass that so the full URL is rendered
        // NOTE: Need to look into security considerations of this
        this.url = this.sanitizer.bypassSecurityTrustResourceUrl(requestedUrl);

    }

    /**
     * The ng on after content init hook
     */
    ngAfterContentInit(): void {
        const eventMethod = window.addEventListener ? 'addEventListener' : 'attachEvent';
        const eventer = window[eventMethod];
        const messageEvent = eventMethod === 'attachEvent' ? 'onmessage' : 'message';
        this.mapReady.next(false);
        /* istanbul ignore next */
        eventer(messageEvent, (e) => {
            // listen for map ready
            if (e.data && e.data.portalExternalPlansLoaded && !this.mapReady.getValue()) {
                // Received plan loaded
                this.mapReady.next(e.data.portalExternalPlansLoaded);
                // Sending down the plan selected from external
                const message = {
                    'portalMapsPlanType': this.planType,
                    'dossierId': this.dossierId
                };
                this.iframeElement.nativeElement.contentWindow.postMessage(message, '*');
            }
            if (e.data.mapsSelectedAmbiti && e.data.mapsSelectedPiano) {
                this.selected.emit(e.data);
            }

            if (e.data.portalFullScreenMap !== undefined) {
                this.isFullScreen = e.data.portalFullScreenMap;
                this.logger.trace('Is full screen', e.data.portalFullScreenMap);
                this.fullScreened.emit(this.isFullScreen);
            }
        }, false);

        const subscriptionMapReady = this.mapReady.pipe(first()).subscribe((mapReady) => {
            this.logger.trace('Map status has beeen changed', mapReady);
            /* istanbul ignore next */
            if (mapReady) {
                // Send the selections
                this.sendStatus();
            }
        });
        this.subscriptions.push(subscriptionMapReady);

    }

    /**
     * The changes hook
     * @param changes The changes
     */

    ngOnChanges(changes: SimpleChanges) {

        const hasChangesFn = this.hasValueChanged(changes);

        if (hasChangesFn('disable') || hasChangesFn('planType') || hasChangesFn('ambiti')) {
            this.sendStatus();
        }

        if (hasChangesFn('language') || hasChangesFn('dossierExtent') || hasChangesFn('planType')) {
            const envPath = environment.name === '' ? 'prod' : environment.name;
            const uui = new Date().getTime().toString()
            const requestedUrl = `assets/map/${envPath}/?uuid=${uui}#?lang=${this.language}&ID_DOSSIER=${this.dossierId}&EXTENT_DOSSIER=${this.dossierExtent}&ID_CONTEXT=${this.contextId}&PLAN_TYPE_DOSSIER=${this.planType}`
            this.url = this.sanitizer.bypassSecurityTrustResourceUrl(requestedUrl);
            this.sendStatus()
        }
    }

    /**
     * The ng on destroy hook
     */
    /* istanbul ignore next */
    ngOnDestroy(): void {
        /* this.logger.trace('Remove subscriptions IFrameComponent'); */
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    private sendStatus() {
        const message = {
            'portalMapsDisableDraw': this.disable,
            'portalMapsPlanType': this.planType,
            'portalMapsAmbiti': this.ambiti,
            'dossierId': this.dossierId,
            'dossierExtent': this.dossierExtent,
            'contextId': this.contextId,
        };
        this.logger.trace('Send a message to', message);
        this.iframeElement.nativeElement.contentWindow.postMessage(message, '*');
    }
}
